import { useApi, useToaster } from "@shopexperts/shared";
import { useMutation, useQueryClient } from "@tanstack/vue-query";
import type { CreatePlanInvitationRequest } from "@shopexperts/legacy-api";
import {
  checkoutLockMessage,
  isInvitationFeatureUnavailable,
  parseInvitationApiError,
} from "./paymentInvitation";
import { planInvitationsQueryKey } from "./useAdminPlanInvitations";

export function useCreatePlanInvitation() {
  const api = useApi();
  const toaster = useToaster();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationKey: ["admin", "experts", "plan-invitations", "create"],
    mutationFn: ({ expertId, data }: { expertId: number; data: CreatePlanInvitationRequest }) => (
      api.admin.expertBilling.createPlanInvitation({ params: { expert: expertId }, body: data })
    ),
    onSuccess: (_invitation, variables) => {
      queryClient.invalidateQueries({ queryKey: planInvitationsQueryKey(variables.expertId) });
      toaster.create({ title: "Payment invitation issued", type: "success" });
    },
    onError: (error: Error) => {
      const details = parseInvitationApiError(error);
      const lockMessage = checkoutLockMessage(error);
      toaster.create({
        title: isInvitationFeatureUnavailable(error)
          ? "Invitation issuing is unavailable"
          : lockMessage
            ? "Invitation is temporarily locked"
          : "Failed to issue payment invitation",
        description: isInvitationFeatureUnavailable(error)
          ? "Expert billing is not enabled on the API yet. Existing invitations remain available."
          : lockMessage ?? details.message ?? "An unexpected error occurred",
        type: "error",
      });
      console.error("[useCreatePlanInvitation] Error:", error);
    },
  });

  return mutation;
}

export default useCreatePlanInvitation;
